import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, TrendingUp, TrendingDown, Filter, Info } from 'lucide-react';
import { calculateStaffing } from '../../utils/erlangC';
import { ServiceLevelParams } from './ServiceLevelControls';

interface ForecastDataPoint {
  interval: string;
  predictedValue: number;
  actualValue?: number;
  upperBound?: number;
  lowerBound?: number;
  headcountNeeded?: number;
  channel?: string;
  team?: string;
}

interface DayData {
  date: string;
  dayName: string;
  totalVolume: number;
  avgHourlyVolume: number;
  peakHour: string;
  peakVolume: number;
  maxHeadcount: number;
  agentHours: number; // Total agent-hours needed for the day (sum of headcount × 0.25 per 15-min interval)
  intervals: ForecastDataPoint[];
  variance?: number;
}

interface CollapsibleForecastTableProps {
  viewMode: 'WEEKLY' | 'MONTHLY';
  forecastData?: any[];
  selectedChannel?: string;
  selectedTeam?: string;
  selectedShift?: string;
  serviceLevelParams: ServiceLevelParams;
}

export default function CollapsibleForecastTable({
  viewMode,
  forecastData = [],
  selectedChannel = 'ALL',
  selectedTeam = 'ALL',
  selectedShift = 'ALL',
  serviceLevelParams,
}: CollapsibleForecastTableProps) {
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  // Check if filters are active
  const hasActiveFilters = selectedChannel !== 'ALL' || selectedTeam !== 'ALL' || selectedShift !== 'ALL';

  // Auto-detect data interval from timestamps (prevents Erlang C inflation)
  const detectedInterval = useMemo((): number => {
    if (!forecastData || forecastData.length < 2) return 15;
    const timestamps = forecastData
      .filter((dp: any) => dp.timestamp)
      .map((dp: any) => new Date(dp.timestamp).getTime())
      .sort((a: number, b: number) => a - b);
    if (timestamps.length < 2) return 15;
    const deltaCounts = new Map<number, number>();
    for (let i = 0; i < Math.min(timestamps.length - 1, 200); i++) {
      const delta = Math.round((timestamps[i + 1] - timestamps[i]) / (1000 * 60));
      if (delta > 0 && delta <= 1440) deltaCounts.set(delta, (deltaCounts.get(delta) || 0) + 1);
    }
    if (deltaCounts.size === 0) return 15;
    let best = 0, maxC = 0;
    deltaCounts.forEach((c, d) => { if (c > maxC) { maxC = c; best = d; } });
    if (best <= 17) return 15;
    if (best <= 45) return 30;
    if (best <= 90) return 60;
    return best;
  }, [forecastData]);

  // Group forecast data by day - uses real forecast data from API
  const dayData = useMemo((): DayData[] => {
    console.log('CollapsibleForecastTable - Processing forecast data:', forecastData?.length || 0, 'data points');

    if (!forecastData || forecastData.length === 0) {
      console.log('CollapsibleForecastTable - No forecast data available');
      return [];
    }

    const days: Map<string, ForecastDataPoint[]> = new Map();

    // Group data points by date
    forecastData.forEach((dataPoint) => {
      // Extract date from timestamp
      const timestamp = new Date(dataPoint.timestamp);
      const dateKey = timestamp.toISOString().split('T')[0];

      // Get or create array for this date
      if (!days.has(dateKey)) {
        days.set(dateKey, []);
      }

      // Calculate headcount needed using Erlang C
      const staffing = calculateStaffing({
        callVolume: dataPoint.predictedValue,
        ahtMinutes: serviceLevelParams.aht,
        targetSLPercent: serviceLevelParams.slaPercent,
        targetAnswerSeconds: serviceLevelParams.answerTime,
        shrinkagePercent: serviceLevelParams.shrinkage,
        intervalMinutes: detectedInterval,
      });

      // Add the interval to the day's data
      days.get(dateKey)!.push({
        interval: dataPoint.interval,
        predictedValue: dataPoint.predictedValue,
        actualValue: dataPoint.actualValue,
        upperBound: dataPoint.upperBound,
        lowerBound: dataPoint.lowerBound,
        headcountNeeded: staffing.requiredAgents,
        channel: selectedChannel !== 'ALL' ? selectedChannel : undefined,
        team: selectedTeam !== 'ALL' ? selectedTeam : undefined,
      });
    });

    console.log('CollapsibleForecastTable - Grouped into days:', days.size);

    // First pass: Calculate total volume by day of week for WoW comparison
    const dayOfWeekNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const volumeByDayOfWeek: Map<number, number[]> = new Map();

    Array.from(days.entries()).forEach(([dateKey, intervals]) => {
      const date = new Date(dateKey);
      const dayOfWeek = date.getDay();
      const totalVolume = intervals.reduce((sum, i) => sum + i.predictedValue, 0);

      if (!volumeByDayOfWeek.has(dayOfWeek)) {
        volumeByDayOfWeek.set(dayOfWeek, []);
      }
      volumeByDayOfWeek.get(dayOfWeek)!.push(totalVolume);
    });

    // Calculate average volume for each day of week
    const avgVolumeByDayOfWeek: Map<number, number> = new Map();
    volumeByDayOfWeek.forEach((volumes, dayOfWeek) => {
      const avg = volumes.reduce((sum, v) => sum + v, 0) / volumes.length;
      avgVolumeByDayOfWeek.set(dayOfWeek, avg);
    });

    // Convert to DayData array and calculate aggregates
    const dayDataArray = Array.from(days.entries()).map(([dateKey, intervals]) => {
      const date = new Date(dateKey);
      const dayOfWeek = date.getDay();
      const totalVolume = intervals.reduce((sum, i) => sum + i.predictedValue, 0);
      const avgHourlyVolume = Math.round(totalVolume / 24);

      // Find peak hour
      const hourlyVolumes = new Map<string, number>();
      intervals.forEach((interval) => {
        const hour = interval.interval.split(':')[0];
        const current = hourlyVolumes.get(hour) || 0;
        hourlyVolumes.set(hour, current + interval.predictedValue);
      });

      let peakHour = '00';
      let peakVolume = 0;
      hourlyVolumes.forEach((volume, hour) => {
        if (volume > peakVolume) {
          peakVolume = volume;
          peakHour = hour;
        }
      });

      // Calculate variance (Week over Week - percentage difference from average for same day of week)
      const avgForDayOfWeek = avgVolumeByDayOfWeek.get(dayOfWeek) || 0;
      const variance = avgForDayOfWeek > 0 ? ((totalVolume - avgForDayOfWeek) / avgForDayOfWeek) * 100 : 0;

      // Calculate max headcount needed for the day
      const maxHeadcount = Math.max(...intervals.map(i => i.headcountNeeded || 0));

      // Calculate total agent-hours needed for the day
      // Each interval is 15 minutes (0.25 hours), so sum headcount × 0.25
      const agentHours = Math.round(
        intervals.reduce((sum, i) => sum + (i.headcountNeeded || 0), 0) * 0.25
      );

      return {
        date: dateKey,
        dayName: dayOfWeekNames[dayOfWeek],
        totalVolume,
        avgHourlyVolume,
        peakHour: `${peakHour}:00`,
        peakVolume: Math.round(peakVolume),
        maxHeadcount,
        agentHours,
        intervals,
        variance,
      };
    });

    // Sort by date
    dayDataArray.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Filter based on view mode
    const now = new Date();
    if (viewMode === 'WEEKLY') {
      // Show only the next 7 days
      const weekEnd = new Date(now);
      weekEnd.setDate(now.getDate() + 7);
      const filtered = dayDataArray.filter(day => {
        const dayDate = new Date(day.date);
        return dayDate >= now && dayDate <= weekEnd;
      });
      console.log('CollapsibleForecastTable - Filtered to WEEKLY view:', filtered.length, 'days');
      return filtered;
    } else {
      // Show only the next 30 days
      const monthEnd = new Date(now);
      monthEnd.setDate(now.getDate() + 30);
      const filtered = dayDataArray.filter(day => {
        const dayDate = new Date(day.date);
        return dayDate >= now && dayDate <= monthEnd;
      });
      console.log('CollapsibleForecastTable - Filtered to MONTHLY view:', filtered.length, 'days');
      return filtered;
    }
  }, [viewMode, forecastData, selectedChannel, selectedTeam, serviceLevelParams, detectedInterval]);

  const toggleDay = (date: string) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDays(newExpanded);
  };

  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {viewMode === 'WEEKLY' ? 'Weekly' : 'Monthly'} Forecast Details
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Click on any day to expand and view all forecasted intervals
            </p>
          </div>
          {hasActiveFilters && (
            <div className="flex items-center space-x-2 text-sm">
              <Filter className="h-4 w-4 text-purple-600" />
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                Filtered by: {selectedChannel !== 'ALL' && selectedChannel}
                {selectedChannel !== 'ALL' && (selectedTeam !== 'ALL' || selectedShift !== 'ALL') && ', '}
                {selectedTeam !== 'ALL' && selectedTeam}
                {selectedTeam !== 'ALL' && selectedShift !== 'ALL' && ', '}
                {selectedShift !== 'ALL' && selectedShift}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-12">
                {/* Chevron column */}
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Day
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Total Volume
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Avg/Hour
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Peak Hour
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Peak Volume
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                Max Headcount
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-purple-700 uppercase tracking-wider">
                <div className="flex items-center justify-end space-x-1">
                  <span>Agent Hours</span>
                  <div className="group relative">
                    <Info className="h-3 w-3 text-purple-400 cursor-help" />
                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-56 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                      Total agent-hours of staffing needed for the day. Divide by shift length (e.g., 8 hrs) to estimate agent-shifts needed.
                    </div>
                  </div>
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                <div className="flex items-center justify-end space-x-1">
                  <span>vs Avg WoW</span>
                  <div className="group relative">
                    <Info className="h-3 w-3 text-gray-400 cursor-help" />
                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-56 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                      Compares this day's volume against the average for the same day of the week (e.g., Monday vs avg Monday)
                    </div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {dayData.map((day) => {
              const isExpanded = expandedDays.has(day.date);
              const isWeekend = day.dayName === 'Saturday' || day.dayName === 'Sunday';

              return (
                <>
                  {/* Day Summary Row */}
                  <tr
                    key={day.date}
                    onClick={() => toggleDay(day.date)}
                    className={`cursor-pointer transition-colors ${
                      isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'
                    } ${isWeekend ? 'bg-gray-50' : ''}`}
                  >
                    <td className="px-6 py-4">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <span className={isWeekend ? 'text-gray-500' : 'font-medium'}>{day.dayName}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                      {day.totalVolume.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      {day.avgHourlyVolume.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {day.peakHour}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {day.peakVolume.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-900 text-right font-bold">
                      {day.maxHeadcount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-900 text-right font-semibold">
                      {day.agentHours.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      {day.variance !== undefined && (
                        <span
                          className={`inline-flex items-center space-x-1 ${
                            day.variance > 0 ? 'text-green-700' : day.variance < 0 ? 'text-red-700' : 'text-gray-600'
                          }`}
                        >
                          {day.variance > 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : day.variance < 0 ? (
                            <TrendingDown className="h-3 w-3" />
                          ) : null}
                          <span className="font-medium">{day.variance > 0 ? '+' : ''}{day.variance.toFixed(1)}%</span>
                        </span>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Intervals */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={10} className="px-0 py-0 bg-gray-50">
                        <div className="px-6 py-4">
                          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                              <h4 className="text-sm font-semibold text-blue-900">
                                15-Minute Interval Forecast for {day.dayName}, {new Date(day.date).toLocaleDateString()}
                              </h4>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100 sticky top-0">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Time</th>
                                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">Forecast</th>
                                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">Actual</th>
                                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">
                                      <div className="flex items-center justify-end space-x-1">
                                        <span>Headcount Needed</span>
                                        <div className="group relative">
                                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                          <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                                            Calculated agents needed based on volume, AHT, and service level targets using Erlang C
                                          </div>
                                        </div>
                                      </div>
                                    </th>
                                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">Lower Bound</th>
                                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">Upper Bound</th>
                                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">Accuracy</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {day.intervals.map((interval, idx) => {
                                    const accuracy = interval.actualValue
                                      ? (1 - Math.abs(interval.predictedValue - interval.actualValue) / interval.predictedValue) * 100
                                      : null;

                                    return (
                                      <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-gray-900 font-medium">{interval.interval}</td>
                                        <td className="px-4 py-2 text-right text-gray-900 font-semibold">
                                          {interval.predictedValue}
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-700">
                                          {interval.actualValue || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-right text-indigo-700 font-semibold">
                                          {interval.headcountNeeded ?? '-'}
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-500 text-xs">
                                          {interval.lowerBound ?? '-'}
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-500 text-xs">
                                          {interval.upperBound ?? '-'}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                          {accuracy !== null && (
                                            <span
                                              className={`text-xs font-medium ${
                                                accuracy >= 90
                                                  ? 'text-green-700'
                                                  : accuracy >= 75
                                                  ? 'text-yellow-700'
                                                  : 'text-red-700'
                                              }`}
                                            >
                                              {accuracy.toFixed(1)}%
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
